<template>
  <v-container fluid class="pa-0">
    <v-toolbar color="primary" dark flat dense>
      <v-toolbar-title overflow-auto> Forecasts </v-toolbar-title>
      <v-spacer />
      <DialogForm
        title="New Forecast: IHR"
        icon="add"
        max-width="900"
        :submit="addForecast"
        submit-text="Save &amp; Run"
      >
        <template #default> <IHRForm v-model="formData" /> </template>
      </DialogForm>
    </v-toolbar>
    <ForecastOutputCharts
      v-if="outputItems.length && forecastIdItems.length"
      :item-id="forecastIdItems"
    />
    <FeathersServiceTable
      service="forecasts"
      :headers="headers"
      :params="tableParams"
      ref="serviceTable"
      :auto-refresh="120"
      :item.sync="tableItems"
    >
      <template #item.status="{ item }">
        <JobIndicator :status="item.status" :progress="item.progress" :error="item.error" />
      </template>

      <template #item.market_name="{ item }">
        {{ marketName(item) }}
      </template>
      <template #item.node_id="{ item }">
        {{ nodeId(item) }}
      </template>

      <template #item.forecast_horizon="{ item }">
        {{ forecastHorizon(item.input) }}
      </template>
      <template #item.forecast_charts="{ item }">
        <Dialog
          icon="insert_chart"
          tooltip="Show Chart"
          title="Forecast Charts"
          :body="{
            component: dialogForecastOutputCharts,
            props: {
              itemId: item.id,
            },
          }"
          :disabled="item.status !== 'DONE'"
          @close="refreshTable()"
        >
        </Dialog>
      </template>
      <template #item.actions="{ item }">
        <DialogForm
          title="Edit Forecast"
          icon="create"
          max-width="800"
          :submit="saveCloneForecast"
          :value="cloneData"
          submit-text="Save &amp; Run"
          @open="fillCloneData(item)"
        >
          <template #activator="{ on: dialogOn }">
            <v-tooltip top>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon small v-on="{ ...dialogOn, ...tooltipOn }">
                  <v-icon>create</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </template>
          <template #default="form">
            <IHRForm v-model="form.item" />
          </template>
        </DialogForm>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn icon small v-on="on" @click="copyForecast(item)">
              <v-icon>file_copy</v-icon>
            </v-btn>
          </template>
          <span>Copy</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn icon small v-on="on" @click="deleteForecast(item.id)">
              <v-icon>delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn
              icon
              small
              :loading="downloadPending === item.id"
              :disabled="Boolean(downloadPending)"
              v-on="on"
              @click="downloadOutputs(item)"
            >
              <v-icon>get_app</v-icon>
            </v-btn>
          </template>
          <span>Download Outputs</span>
        </v-tooltip>
      </template>
    </FeathersServiceTable>
  </v-container>
</template>

<script>
import { DateTime } from "luxon";
import Papa from "papaparse";
import download from "downloadjs";
import { mapActions, mapMutations } from "vuex";
import filters from "@/mixins/filters";
import Chart from "@/components/charts/Plotly.vue";
import { deepClone } from "@/plugins/utils";
import { migrateLegacyAncillaryLabels } from "@/utils/legacyAncillary";
import ForecastOutputCharts from "@/components/ForecastOutputCharts.vue";

const defaultFormData = {
  type: "IHR",
  input: {
    settings: {
      type: "IHR",
      start_of_forecast_date: DateTime.utc().plus({ years: 1 }).startOf("year").toISO(),
      end_of_forecast_date: DateTime.utc().plus({ years: 5 }).startOf("year").toISO(),
      profile: "H",
      hfv: -1,
      lfv: -1,
      forecast_limit_lag: false,
      max_forecast_limit: 0,
      min_forecast_limit: 0,
    },
    energy_ancillary_inputs: {
      market_name: null,
      node_id: null,
      index: null,
      shaping_year: 2019,
      year_start: null,
      year_end: null,
      zone_id: null,
      energy_settings: {
        energy_price_source: "market",
        profile: [],
      },
      ancillary_settings: {
        active: false,
        ancillary_price_source: "market",
        ancillary_price_labels: ["primary", "secondary", "tertiary"],
        profile: [],
      },
    },
    fuel_price_inputs: {
      fuel_delivery_charge: 0,
      fuel_price_forecast: {
        fuel_price_source: "default",
        fuel_type: "NG",
        values: [],
        fuel_forecast_id: null,
      },
      historical_fuel_price: {
        fuel_price_source: "default",
        fuel_type: "NG",
        profile: [],
        fuel_historical_id: null,
      },
    },
  },
};

export default {
  name: "Forecasts",
  components: {
    Dialog: () => import("@/components/Dialog.vue"),
    DialogForm: () => import("@/components/DialogForm.vue"),
    IHRForm: () => import("@/components/IHRForm.vue"),
    JobIndicator: () => import("@/components/JobIndicator.vue"),
    FeathersServiceTable: () => import("@/components/FeathersServiceTable.vue"),
    ForecastOutputCharts: () => import("@/components/ForecastOutputCharts.vue"),
  },
  mixins: [filters],
  props: {},
  data() {
    return {
      tableItems: null,
      outputItems: [],
      chartKey: 1,
      forecastIdItems: [],
      headers: [
        { text: "ID", value: "id" },
        { text: "Method", value: "type" },
        { text: "Market Name", value: "market_name" },
        { text: "Node ID", value: "node_id" },
        { text: "Forecast Horizon", value: "forecast_horizon" },
        { text: "Status", value: "status" },
        { text: "Forecast Charts", value: "forecast_charts" },
        { text: "", value: "actions" },
        { text: "", value: "download" },
      ],
      comodities: ["Energy"],
      formData: defaultFormData,
      cloneData: {},
      chartLayout: {
        xaxis: {
          autorange: true,
          rangeslider: {},
          type: "date",
        },
      },
      tableParams: {
        query: {
          $sort: { id: -1 },
          $select: ["id", "type", "input", "status", "error", "progress"],
        },
      },
      downloadPending: false,
      statusIcon: {
        ERROR: { color: "red", icon: "error" },
        IN_PROGRESS: {},
        DONE: { color: "green", icon: "check_circle" },
        NOT_STARTED: { color: "", icon: "not_started" },
      },
    };
  },
  computed: {
    dialogForecastOutputCharts() {
      return ForecastOutputCharts;
    },
    items() {
      if (this.tableItems) {
        return this.tableItems;
      }
      return [];
    },
    plotlyChart() {
      return Chart;
    },
  },
  watch: {
    items(val) {
      this.outputItems = val.reduce((acc, item) => {
        if (item.status === "DONE") {
          acc.push(item);
        }
        return acc;
      }, []);
      this.forecastIdItems = this.outputItems.map((el) => el.id);
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      createForecast: "forecasts/create",
      deleteForecast: "forecasts/confirmRemove",
      getOutput: "forecasts/get",
      findOutput: "forecasts/find",
      saveForecast: "forecasts/patch",
    }),
    ...mapMutations({
      removeForecast: "forecasts/removeItem",
      updateForecast: "forecasts/updateItem",
    }),
    addForecast() {
      console.debug("Creating forecast", this.formData);
      return this.createForecast(this.formData);
    },
    downloadableOutputs(outputs) {
      // The IHR engine only stores a `profile` bucket (sub-hourly, e.g. 5min/30min)
      // when the forecast was generated with a granularity finer than hourly.
      const rows = outputs.profile || outputs.hourly;
      return rows.map((element) => {
        const row = {
          timestamp: element.timestamp,
          energy_price_low: element.energy_price.low,
          energy_price_base: element.energy_price.base,
          energy_price_high: element.energy_price.high,
        };
        Object.entries(element.reserves || {}).forEach(([label, values]) => {
          Object.entries(values).forEach(([band, value]) => {
            row[`${label}_${band}`] = value;
          });
        });
        return row;
      });
    },
    refreshTable() {
      this.$refs.serviceTable.findService();
    },
    downloadOutputs(item) {
      this.downloadPending = item.id;
      this.findOutput({
        query: { id: item.id, $select: ["id", "output"] },
      })
        .then((res) => {
          if (res.total && res.data[0].output) {
            const { market_name } = item.input.energy_ancillary_inputs;
            const csv = Papa.unparse(this.downloadableOutputs(res.data[0].output));
            const { fuel_type } = item.input.fuel_price_inputs.fuel_price_forecast;
            const startDate = DateTime.fromISO(
              item.input.settings.start_of_forecast_date
            ).toISODate();
            const endDate = DateTime.fromISO(item.input.settings.end_of_forecast_date).toISODate();
            download(
              csv,
              `${market_name}_${fuel_type}_${startDate}_${endDate}_output.csv`,
              "text/csv"
            );
          }
          this.downloadPending = false;
          this.removeForecast(item);
          this.refreshTable();
        })
        .catch(() => {
          this.downloadPending = false;
        });
    },
    forecastHorizon(input) {
      return input.settings
        ? `${DateTime.fromISO(input.settings.start_of_forecast_date).toLocaleString(
            DateTime.DATE_SHORT
          )} - ${DateTime.fromISO(input.settings.end_of_forecast_date).toLocaleString(
            DateTime.DATE_SHORT
          )}`
        : `${DateTime.fromISO(input.start_date).toLocaleString(
            DateTime.DATE_SHORT
          )} - ${DateTime.fromISO(input.end_date).toLocaleString(DateTime.DATE_SHORT)}`;
    },
    fillCloneData(item) {
      this.cloneData = deepClone(item);
    },
    saveCloneForecast() {
      this.cloneData.status = "NOT_STARTED";
      return this.saveForecast([this.cloneData.id, this.cloneData, {}]);
    },
    copyForecast(item) {
      const { id, ...settings } = item;
      delete settings.output;
      const cloned = deepClone(settings);
      if (cloned.input.energy_ancillary_inputs) {
        const { market_name: marketName, ancillary_settings: ancillarySettings } =
          cloned.input.energy_ancillary_inputs;
        migrateLegacyAncillaryLabels(marketName, ancillarySettings);
      }
      this.createForecast({ ...cloned, status: "NOT_STARTED" });
    },
    marketName(item) {
      if (item.input.energy_ancillary_inputs) {
        return item.input.energy_ancillary_inputs.market_name || null;
      }
      if (item.input.market) {
        return item.input.market.name;
      }
      return null;
    },
    nodeId(item) {
      if (item.input.energy_ancillary_inputs) {
        return item.input.energy_ancillary_inputs.node_id || null;
      }
      if (item.input.market) {
        return item.input.market.node;
      }
      return null;
    },
  },
};
</script>

<style></style>
